import LockPersonRoundedIcon from '@mui/icons-material/LockPersonRounded';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Collapse from '@mui/material/Collapse';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import * as React from 'react';

import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded';
import { useGetSessionQuery } from '../../api/apiSlice';

import ListItemText from '@mui/material/ListItemText';
import TokenInfo from '../shared/TokenInfo';

import { IconButton } from '@mui/material';
import AvatarComponent from './AvatarComponent';

export default function UserMenu({ mode, toggleColorMode }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [featuresOpen, setFeaturesOpen] = React.useState(false);
  const [subMenuOpen, setSubMenuOpen] = React.useState(false);
  const {
    data: sessionData,
    isLoading: sessionLoading,
    error: sessionError,
  } = useGetSessionQuery();

  const handleLogout = () => {
    const returnTo = encodeURIComponent('/'); // Change this as needed
    window.location.href = `/auth/end-session?returnTo=${returnTo}`;
  };

  const isAdmin =
    (sessionData && sessionData?.claims?.['accounts.superuser'] === 'True') ||
    false;
  const email = (sessionData && sessionData?.claims?.email) || '';
  const exp = sessionData && sessionData?.claims?.exp;
  const sid = (sessionData && sessionData?.claims?.sid) || '';

  const open = Boolean(anchorEl);

  const anchorRef = React.useRef(null);
  const subMenuRef = React.useRef(null);

  const handleClick = (event) => {
    setFeaturesOpen(true);
  };

  const handleClose = (event) => {
    if (
      anchorRef.current.contains(event.target) ||
      (subMenuRef.current && subMenuRef.current.contains(event.target))
    ) {
      return;
    }
    setAnchorEl(null);
    setFeaturesOpen(false);
    setSubMenuOpen(false);
  };

  const handleFeaturesEnter = () => {
    setTimeout(() => {
      setFeaturesOpen(true);
    }, 500);
  };

  const handleFeaturesLeave = (event) => {
    if (
      anchorRef.current.contains(event.relatedTarget) ||
      (subMenuRef.current && subMenuRef.current.contains(event.relatedTarget))
    ) {
      return;
    }
    setFeaturesOpen(false);
    setSubMenuOpen(false);
  };

  const handleSubMenuEnter = () => {
    setSubMenuOpen(true);
  };

  const handleSubMenuLeave = (event) => {
    if (
      anchorRef.current.contains(event.relatedTarget) ||
      (subMenuRef.current && subMenuRef.current.contains(event.relatedTarget))
    ) {
      return;
    }
    setSubMenuOpen(false);
  };

  return (
    <React.Fragment>
      <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
        <IconButton
          onClick={handleClick}
          size="small"
          ref={anchorRef}
          aria-controls={open ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          sx={{ borderRadius: 1 }}
        >
          <AvatarComponent string={email} />
        </IconButton>
        <Popper
          open={featuresOpen}
          anchorEl={anchorRef.current}
          role={undefined}
          placement="bottom-start"
          transition
          sx={{ zIndex: 1301 }}
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom-start' ? 'left top' : 'left bottom',
              }}
            >
              <Paper
                elevation={0}
                onMouseEnter={handleFeaturesEnter}
                onMouseLeave={handleFeaturesLeave}
                variant="outlined"
                sx={{ overflow: 'visible' }}
              >
                <ClickAwayListener onClickAway={handleClose}>
                  <div style={{ display: 'flex' }}>
                    <Collapse
                      in={subMenuOpen}
                      orientation="horizontal"
                      timeout="auto"
                      unmountOnExit
                    >
                      <Paper ref={subMenuRef} elevation={0}>
                        {sessionData && (
                          <TokenInfo
                            isAdmin={isAdmin}
                            email={email}
                            exp={exp}
                            sid={sid}
                          />
                        )}
                      </Paper>
                    </Collapse>
                    <MenuList
                      autoFocusItem={featuresOpen}
                      sx={{ p: 1 }}
                      id="composition-menu"
                      aria-labelledby="composition-button"
                    >
                      <MenuItem
                        onMouseEnter={handleSubMenuEnter}
                        onMouseLeave={handleSubMenuLeave}
                        sx={{
                          py: '6px',
                          px: '12px',
                          borderRadius: '5px',
                          ':hover': {
                            py: '6px',
                            px: '12px',
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <LockPersonRoundedIcon
                                sx={{
                                  color:
                                    mode === 'light'
                                      ? 'hsl(0, 0%, 20%)'
                                      : 'hsl(0, 0%, 85%)',
                                }}
                                fontSize="small"
                              />
                              <Typography
                                variant="body1"
                                fontSize="0.8rem"
                                sx={{
                                  ml: 1,
                                  fontWeight: 'bold',
                                  flexGrow: 1,
                                  color:
                                    mode === 'light'
                                      ? 'hsl(0, 0%, 20%)'
                                      : 'hsl(0, 0%, 85%)',
                                }}
                              >
                                User
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography
                                variant="subtitle2"
                                sx={{ color: 'gray', fontSize: '0.8rem' }}
                              >
                                Current User
                              </Typography>
                            </Box>
                          }
                        />
                      </MenuItem>

                      <MenuItem
                        onClick={toggleColorMode}
                        sx={{
                          py: '6px',
                          px: '12px',
                          borderRadius: '5px',
                          ':hover': {
                            py: '6px',
                            px: '12px',
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {mode === 'dark' ? (
                                <WbSunnyRoundedIcon
                                  fontSize="small"
                                  sx={{ color: 'orange' }}
                                />
                              ) : (
                                <DarkModeRoundedIcon fontSize="small" />
                              )}
                              <Typography
                                variant="body1"
                                fontSize="0.8rem"
                                sx={{
                                  ml: 1,
                                  fontWeight: 'bold',
                                  flexGrow: 1,
                                  color:
                                    mode === 'light'
                                      ? 'hsl(0, 0%, 20%)'
                                      : 'hsl(0, 0%, 85%)',
                                }}
                              >
                                {mode === 'light' ? 'Dark Mode' : 'Light Mode'}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography
                                variant="subtitle2"
                                sx={{ color: 'gray', fontSize: '0.8rem' }}
                              >
                                Toggle between light and dark mode.
                              </Typography>
                            </Box>
                          }
                        />
                      </MenuItem>
                      <MenuItem
                        onClick={handleLogout}
                        sx={{
                          py: '6px',
                          px: '12px',
                          borderRadius: '5px',
                          ':hover': {
                            py: '6px',
                            px: '12px',
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <LogoutRoundedIcon
                                fontSize="small"
                                sx={{
                                  color:
                                    mode === 'light'
                                      ? 'hsl(0, 0%, 20%)'
                                      : 'hsl(0, 0%, 85%)',
                                }}
                              />
                              <Typography
                                variant="body1"
                                fontSize="0.8rem"
                                sx={{
                                  ml: 1,
                                  fontWeight: 'bold',
                                  flexGrow: 1,
                                  color:
                                    mode === 'light'
                                      ? 'hsl(0, 0%, 20%)'
                                      : 'hsl(0, 0%, 85%)',
                                }}
                              >
                                Log out
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography
                                variant="subtitle2"
                                sx={{ color: 'gray', fontSize: '0.8rem' }}
                              >
                                End current session and log out.
                              </Typography>
                            </Box>
                          }
                        />
                      </MenuItem>
                    </MenuList>
                  </div>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Box>
    </React.Fragment>
  );
}
